import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Loader2, Save, Plus, X, CheckCircle2, MapPin, Trash2, AlertTriangle, ArrowLeftRight, Building2, HardHat, Upload, FileText, Users, XCircle } from 'lucide-react';
import AvailabilityCalendar from '../components/AvailabilityCalendar';
import PortfolioGallery from '../components/PortfolioGallery';
import { KboStatusCard } from '../components/profile/KboStatusCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProfilePage = () => {
  const { t } = useTranslation();
  const { user, updateUser, isContractor, isCompany, switchRole, logout } = useAuth();
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [profileDocs, setProfileDocs] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    vat_number: '',
    onss_number: '',
    employee_count: 0,
    detached_workers: [],
    phone: '',
    address: '',
    postal_code: '',
    city: '',
    country: 'Belgium',
    description: '',
    latitude: '',
    longitude: '',
    service_radius: 50,
    trades: [],
    years_experience: 0,
    certifications: [],
    is_available: true,
    email_new_jobs: true
  });
  const [newCertification, setNewCertification] = useState('');
  const [geocoding, setGeocoding] = useState(false);

  useEffect(() => {
    const fetchTrades = async () => {
      try {
        const res = await axios.get(`${API}/trades`);
        setTrades(res.data);
      } catch (error) {
        console.error('Error fetching trades:', error);
      }
    };
    fetchTrades();
  }, []);

  useEffect(() => {
    if (user) {
      setFormData({
        company_name: user.company_name || '',
        vat_number: user.vat_number || '',
        onss_number: user.onss_number || '',
        employee_count: user.employee_count || 0,
        detached_workers: user.detached_workers || [],
        phone: user.phone || '',
        address: user.address || '',
        postal_code: user.postal_code || '',
        city: user.city || '',
        country: user.country || 'Belgium',
        description: user.description || '',
        latitude: user.latitude || '',
        longitude: user.longitude || '',
        service_radius: user.service_radius || 50,
        trades: user.trades || [],
        years_experience: user.years_experience || 0,
        certifications: user.certifications || [],
        is_available: user.is_available !== false,
        email_new_jobs: user.email_new_jobs === true
      });
    }
  }, [user]);

  // Fetch profile documents
  useEffect(() => {
    if (user) {
      axios.get(`${API}/profile/documents`, { withCredentials: true })
        .then(res => setProfileDocs(res.data))
        .catch(() => {});
    }
  }, [user]);

  const handleUploadDoc = async (docType) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.jpg,.jpeg,.png,.doc,.docx';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploadingDoc(docType);
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API}/profile/documents/upload?doc_type=${docType}`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
        setProfileDocs(prev => [{ id: 'temp', doc_type: docType, original_filename: file.name, storage_path: res.data.path, uploaded_at: new Date().toISOString() }, ...prev]);
        toast.success('Document uploadé');
      } catch (err) { toast.error('Erreur lors de l\'upload'); }
      finally { setUploadingDoc(false); }
    };
    input.click();
  };

  const handleDeleteDoc = async (docId) => {
    try {
      await axios.delete(`${API}/profile/documents/${docId}`, { withCredentials: true });
      setProfileDocs(prev => prev.filter(d => d.id !== docId));
      toast.success('Document supprimé');
    } catch (err) { toast.error('Erreur'); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTradeToggle = (tradeId) => {
    setFormData(prev => ({
      ...prev,
      trades: prev.trades.includes(tradeId)
        ? prev.trades.filter(t => t !== tradeId)
        : [...prev.trades, tradeId]
    }));
  };

  const addCertification = () => {
    if (newCertification.trim()) {
      setFormData(prev => ({
        ...prev,
        certifications: [...prev.certifications, newCertification.trim()]
      }));
      setNewCertification('');
    }
  };

  const removeCertification = (index) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.put(`${API}/users/profile`, formData, { withCredentials: true });
      updateUser(response.data);
      toast.success('Profil mis à jour avec succès!');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du profil');
    } finally {
      setLoading(false);
    }
  };

  const handleGeocode = async () => {
    setGeocoding(true);
    try {
      const params = new URLSearchParams();
      if (formData.address) params.append('address', formData.address);
      if (formData.postal_code) params.append('postal_code', formData.postal_code);
      if (formData.city) params.append('city', formData.city);
      if (formData.country) params.append('country', formData.country);
      
      const res = await axios.get(`${API}/geocode?${params.toString()}`);
      if (res.data.latitude && res.data.longitude) {
        setFormData(prev => ({
          ...prev,
          latitude: res.data.latitude,
          longitude: res.data.longitude
        }));
        toast.success(`Position trouvée: ${res.data.display_name?.substring(0, 60)}...`);
      } else {
        toast.error('Adresse non trouvée. Vérifiez les informations saisies.');
      }
    } catch (err) {
      toast.error('Erreur de géolocalisation');
    } finally {
      setGeocoding(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/users/me`, { withCredentials: true });
      toast.success('Compte supprimé avec succès');
      logout();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleSwitchRole = async (targetRole) => {
    if (switching) return;
    if ((targetRole === 'company' && isCompany) || (targetRole === 'contractor' && isContractor)) return;
    setSwitching(true);
    try {
      await switchRole(targetRole);
      toast.success(
        targetRole === 'company' 
          ? 'Mode Donneur d\'ordre activé'
          : 'Mode Sous-traitant activé'
      );
    } catch (err) {
      console.error('Switch role error:', err?.response?.status, err?.response?.data, err?.message);
      const msg = err?.response?.data?.detail || err?.message || 'Erreur inconnue';
      toast.error(`Erreur: ${msg}`);
    } finally {
      setSwitching(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="font-heading font-bold text-3xl text-[#111827]" data-testid="profile-title">
            {t('profile.title')}
          </h1>
          <p className="text-[#4B5563] mt-1">Gérez les informations de votre entreprise</p>
        </div>

        {/* Role Toggle Slider - Removed: managed in Dashboard */}

        <form onSubmit={handleSubmit}>
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="font-heading">Informations générales</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="company_name">{t('auth.companyName')}</Label>
                  <Input
                    id="company_name"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    className="input-field"
                    data-testid="profile-company-name"
                  />
                </div>
                <div>
                  <Label htmlFor="vat_number">{t('profile.vatNumber')}</Label>
                  <div className="flex gap-0">
                    <select
                      className="input-field rounded-r-none border-r-0 w-20 text-sm font-mono bg-[#F9FAFB]"
                      value={formData.vat_number ? formData.vat_number.substring(0, 2) : 'BE'}
                      onChange={(e) => {
                        const prefix = e.target.value;
                        const current = formData.vat_number || '';
                        const numPart = current.replace(/^(BE|LU|FR)/i, '');
                        setFormData(prev => ({ ...prev, vat_number: prefix + numPart }));
                      }}
                      data-testid="profile-vat-prefix"
                    >
                      <option value="BE">BE</option>
                      <option value="LU">LU</option>
                      <option value="FR">FR</option>
                    </select>
                    <Input
                      id="vat_number"
                      name="vat_number"
                      value={(formData.vat_number || '').replace(/^(BE|LU|FR)/i, '')}
                      onChange={(e) => {
                        const prefix = (formData.vat_number || 'BE').substring(0, 2);
                        setFormData(prev => ({ ...prev, vat_number: prefix + e.target.value }));
                      }}
                      placeholder="0123.456.789"
                      className="input-field rounded-l-none flex-1"
                      data-testid="profile-vat"
                    />
                  </div>
                </div>
              </div>

              <KboStatusCard user={user} onUpdated={updateUser} />

              {/* Personnel & Documents - Sous-traitants */}
              {isContractor && (
                <div className="border border-[#E5E7EB] rounded-lg p-4 bg-[#F9FAFB] space-y-4">
                  <Label className="flex items-center gap-2 text-base font-semibold">
                    <Users className="w-4 h-4 text-[#1E3A8A]" />
                    Personnel & Conformité
                  </Label>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Nombre de personnel</Label>
                      <Input type="number" min="0" name="employee_count" value={formData.employee_count} onChange={handleChange} className="input-field h-9" data-testid="profile-employee-count" />
                    </div>
                    <div>
                      <Label className="text-xs">N° ONSS <span className="text-[#6B7280] font-normal">(recommandé)</span></Label>
                      <Input name="onss_number" value={formData.onss_number} onChange={handleChange} placeholder="123-4567890-12" className="input-field h-9" data-testid="profile-onss" />
                    </div>
                  </div>

                  {/* Personnel détaché */}
                  <div className="border-t pt-3">
                    <Label className="text-sm font-semibold mb-2 block">Personnel détaché par nationalité</Label>
                    {formData.detached_workers.length > 0 && (
                      <div className="space-y-1 mb-2">
                        {formData.detached_workers.map((dw, idx) => (
                          <div key={`${dw.country}-${dw.count}-${idx}`} className="flex items-center gap-2 bg-white p-2 rounded border text-sm" data-testid={`detached-worker-${idx}`}>
                            <Badge className="bg-[#1E3A8A]/10 text-[#1E3A8A] text-xs">{dw.country}</Badge>
                            <span className="flex-1">{dw.count} ouvrier{dw.count > 1 ? 's' : ''}</span>
                            <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => setFormData(prev => ({...prev, detached_workers: prev.detached_workers.filter((_, i) => i !== idx)}))} data-testid={`remove-detached-${idx}`}>
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex items-end gap-2">
                      <select id="profile-detached-country" className="input-field text-sm h-9 flex-1" defaultValue="" data-testid="detached-country-select">
                        <option value="" disabled>Pays</option>
                        <option value="Belgique">Belgique</option><option value="Luxembourg">Luxembourg</option><option value="France">France</option>
                        <option value="Allemagne">Allemagne</option><option value="Pays-Bas">Pays-Bas</option><option value="Pologne">Pologne</option>
                        <option value="Roumanie">Roumanie</option><option value="Portugal">Portugal</option><option value="Italie">Italie</option>
                        <option value="Espagne">Espagne</option><option value="Autre UE">Autre UE</option><option value="Hors UE">Hors UE</option>
                      </select>
                      <Input id="profile-detached-count" type="number" min="1" placeholder="Nb" className="input-field h-9 w-16" data-testid="detached-count-input" />
                      <Button type="button" variant="outline" size="sm" className="h-9 gap-1 text-xs" onClick={() => {
                        const cEl = document.getElementById('profile-detached-country');
                        const nEl = document.getElementById('profile-detached-count');
                        if (!cEl?.value) return toast.error('Sélectionnez un pays');
                        if (!parseInt(nEl?.value)) return toast.error("Indiquez le nombre");
                        setFormData(prev => ({...prev, detached_workers: [...prev.detached_workers, {country: cEl.value, count: parseInt(nEl.value)}]}));
                        cEl.value = ''; nEl.value = '';
                      }} data-testid="add-detached-btn">
                        <Plus className="w-3 h-3" /> Ajouter
                      </Button>
                    </div>
                    <p className="text-xs text-amber-700 mt-2 bg-amber-50 p-2 rounded border border-amber-200">
                      <AlertTriangle className="w-3 h-3 inline mr-1" />
                      Déclaration <strong>Limosa</strong> obligatoire pour tout personnel détaché en Belgique.
                    </p>
                  </div>

                  {/* Upload documents */}
                  <div className="border-t pt-3">
                    <Label className="text-sm font-semibold mb-2 block">Documents (Limosa, ONSS, URSSAF, Assurances...)</Label>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {['Limosa', 'ONSS', 'URSSAF', 'Assurance RC', 'Autre'].map(docType => (
                        <Button
                          key={docType}
                          type="button"
                          variant="outline"
                          size="sm"
                          className="gap-1 text-xs"
                          onClick={() => handleUploadDoc(docType)}
                          disabled={uploadingDoc === docType}
                          data-testid={`upload-${docType.toLowerCase().replace(/\s/g, '-')}`}
                        >
                          {uploadingDoc === docType ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                          {docType}
                        </Button>
                      ))}
                    </div>
                    {profileDocs.length > 0 && (
                      <div className="space-y-1">
                        {profileDocs.map(doc => (
                          <div key={doc.id} className="flex items-center gap-2 bg-white p-2 rounded border text-sm">
                            <FileText className="w-4 h-4 text-[#1E3A8A] flex-shrink-0" />
                            <Badge className="bg-[#1E3A8A]/10 text-[#1E3A8A] text-xs">{doc.doc_type}</Badge>
                            <span className="flex-1 truncate text-xs text-[#374151]">{doc.original_filename}</span>
                            <a href={`${API.replace('/api','')}/api/files/${doc.storage_path}`} target="_blank" rel="noreferrer" className="text-[#1E3A8A] text-xs hover:underline">Voir</a>
                            <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => handleDeleteDoc(doc.id)}>
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">{t('profile.phone')}</Label>
                  <div className="flex gap-0">
                    <select
                      className="input-field rounded-r-none border-r-0 w-24 text-sm bg-[#F9FAFB]"
                      value={(() => {
                        const p = formData.phone || '';
                        if (p.startsWith('+352')) return '+352';
                        if (p.startsWith('+33')) return '+33';
                        if (p.startsWith('+49')) return '+49';
                        if (p.startsWith('+31')) return '+31';
                        return '+32';
                      })()}
                      onChange={(e) => {
                        const prefix = e.target.value;
                        const current = formData.phone || '';
                        const numPart = current.replace(/^\+\d{2,3}\s?/, '');
                        setFormData(prev => ({ ...prev, phone: prefix + ' ' + numPart }));
                      }}
                      data-testid="profile-phone-prefix"
                    >
                      <option value="+32">BE +32</option>
                      <option value="+352">LU +352</option>
                      <option value="+33">FR +33</option>
                      <option value="+49">DE +49</option>
                      <option value="+31">NL +31</option>
                    </select>
                    <Input
                      id="phone"
                      value={(formData.phone || '').replace(/^\+\d{2,3}\s?/, '')}
                      onChange={(e) => {
                        const current = formData.phone || '+32';
                        const prefixMatch = current.match(/^\+\d{2,3}/);
                        const prefix = prefixMatch ? prefixMatch[0] : '+32';
                        setFormData(prev => ({ ...prev, phone: prefix + ' ' + e.target.value }));
                      }}
                      placeholder="xxx xx xx xx"
                      className="input-field rounded-l-none flex-1"
                      data-testid="profile-phone"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="country">{t('jobs.country')}</Label>
                  <Select 
                    value={formData.country} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, country: value }))}
                  >
                    <SelectTrigger data-testid="profile-country">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Belgium">Belgique</SelectItem>
                      <SelectItem value="Luxembourg">Luxembourg</SelectItem>
                      <SelectItem value="France">France</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="city">{t('jobs.city')}</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="input-field"
                    data-testid="profile-city"
                  />
                </div>
                <div>
                  <Label htmlFor="postal_code">Code postal</Label>
                  <Input
                    id="postal_code"
                    name="postal_code"
                    value={formData.postal_code}
                    onChange={handleChange}
                    placeholder="1000"
                    className="input-field"
                    data-testid="profile-postal-code"
                  />
                </div>
                <div>
                  <Label htmlFor="address">{t('profile.address')}</Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Rue de la Loi 16"
                    className="input-field"
                    data-testid="profile-address"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="description">{t('profile.description')}</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Décrivez votre entreprise, vos services, votre expérience..."
                  className="min-h-[120px] input-field"
                  data-testid="profile-description"
                />
              </div>
            </CardContent>
          </Card>

          {isContractor && (
            <>
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="font-heading">{t('contractors.trades')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {trades.map((trade) => (
                      <div key={trade.id} className="flex items-center gap-2">
                        <Checkbox
                          id={trade.id}
                          checked={formData.trades.includes(trade.id)}
                          onCheckedChange={() => handleTradeToggle(trade.id)}
                          data-testid={`trade-${trade.id}`}
                        />
                        <label htmlFor={trade.id} className="text-sm cursor-pointer">
                          {t(`trades.${trade.id}`) || trade.name_fr}
                        </label>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="font-heading">Expérience & Disponibilité</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="years_experience">{t('contractors.experience')}</Label>
                      <Input
                        id="years_experience"
                        name="years_experience"
                        type="number"
                        min="0"
                        value={formData.years_experience}
                        onChange={handleChange}
                        className="input-field"
                        data-testid="profile-experience"
                      />
                    </div>
                    <div>
                      <Label htmlFor="service_radius">{t('contractors.serviceRadius')} (km)</Label>
                      <Input
                        id="service_radius"
                        name="service_radius"
                        type="number"
                        min="1"
                        max="500"
                        value={formData.service_radius}
                        onChange={handleChange}
                        className="input-field"
                        data-testid="profile-radius"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-[#F9FAFB] rounded-md">
                    <div>
                      <p className="font-medium">{t('contractors.availability')}</p>
                      <p className="text-sm text-[#4B5563]">Indiquez si vous êtes disponible pour de nouveaux projets</p>
                    </div>
                    <Switch
                      checked={formData.is_available}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_available: checked }))}
                      data-testid="profile-available"
                    />
                  </div>

                  <div id="notifications" className={`flex items-center justify-between p-4 rounded-md border ${formData.email_new_jobs ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                    <div>
                      <p className="font-medium flex items-center gap-2">
                        Alertes email nouveaux chantiers
                        {!formData.email_new_jobs && <span className="text-[10px] uppercase tracking-wide bg-[#F59E0B] text-white px-2 py-0.5 rounded-full">Recommandé</span>}
                      </p>
                      <p className="text-sm text-[#4B5563]">Recevez un email à chaque chantier publié sur BuildConnect pour postuler en premier</p>
                    </div>
                    <Switch
                      checked={formData.email_new_jobs}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, email_new_jobs: checked }))}
                      data-testid="profile-email-new-jobs"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="font-heading">{t('contractors.certifications')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2 mb-4">
                    <Input
                      placeholder="Ajouter une certification..."
                      value={newCertification}
                      onChange={(e) => setNewCertification(e.target.value)}
                      className="input-field"
                      data-testid="profile-new-cert"
                    />
                    <Button type="button" onClick={addCertification} variant="outline">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.certifications.map((cert, index) => (
                      <Badge key={cert} variant="secondary" className="flex items-center gap-1 px-3 py-1">
                        {cert}
                        <button type="button" onClick={() => removeCertification(index)}>
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <div className="mb-6">
                <AvailabilityCalendar userId={user?.id} editable={true} />
              </div>

              <div className="mb-6">
                <PortfolioGallery userId={user?.id} editable={true} />
              </div>
            </>
          )}

          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="font-heading">Localisation GPS</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#4B5563] mb-4">
                La localisation est utilisée pour la carte interactive et la recherche par rayon
              </p>
              <div className="flex gap-2 mb-4">
                <Button
                  type="button"
                  variant="outline"
                  className="gap-2"
                  onClick={handleGeocode}
                  disabled={geocoding || (!formData.address && !formData.postal_code && !formData.city)}
                  data-testid="geocode-button"
                >
                  <MapPin className="w-4 h-4" />
                  {geocoding ? 'Recherche...' : 'Localiser depuis l\'adresse'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="gap-2"
                  onClick={() => {
                    if (navigator.geolocation) {
                      navigator.geolocation.getCurrentPosition(
                        (pos) => {
                          setFormData(prev => ({
                            ...prev,
                            latitude: pos.coords.latitude,
                            longitude: pos.coords.longitude
                          }));
                          toast.success('Position GPS détectée');
                        },
                        () => toast.error('Géolocalisation refusée par le navigateur')
                      );
                    }
                  }}
                  data-testid="gps-detect-button"
                >
                  Utiliser ma position GPS
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="latitude">Latitude</Label>
                  <Input
                    id="latitude"
                    name="latitude"
                    type="number"
                    step="any"
                    placeholder="ex: 50.8503"
                    value={formData.latitude}
                    onChange={handleChange}
                    className="input-field"
                    data-testid="profile-lat"
                  />
                </div>
                <div>
                  <Label htmlFor="longitude">Longitude</Label>
                  <Input
                    id="longitude"
                    name="longitude"
                    type="number"
                    step="any"
                    placeholder="ex: 4.3517"
                    value={formData.longitude}
                    onChange={handleChange}
                    className="input-field"
                    data-testid="profile-lng"
                  />
                </div>
              </div>
              {formData.latitude && formData.longitude && (
                <p className="text-xs text-emerald-600 mt-2 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Position définie: {Number(formData.latitude).toFixed(4)}, {Number(formData.longitude).toFixed(4)}
                </p>
              )}
            </CardContent>
          </Card>

          <Button 
            type="submit" 
            className="btn-primary w-full" 
            disabled={loading}
            data-testid="save-profile"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Enregistrement...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {t('profile.updateProfile')}
              </>
            )}
          </Button>
        </form>

        {/* Delete Account Section */}
        <Card className="mt-8 border-red-200 bg-red-50/50">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-red-800 mb-1">Zone de danger</h3>
                <p className="text-sm text-red-600 mb-3">
                  La suppression de votre compte est irr&eacute;versible. Toutes vos donn&eacute;es, chantiers, candidatures et messages seront d&eacute;finitivement effac&eacute;s.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-100 gap-2"
                  onClick={() => setShowDeleteConfirm(true)}
                  data-testid="delete-account-button"
                >
                  <Trash2 className="w-4 h-4" />
                  Supprimer mon compte
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowDeleteConfirm(false)}>
            <Card className="max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="delete-confirm-modal">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="font-heading font-bold text-xl text-[#111827] mb-2">Confirmer la suppression</h3>
                <p className="text-sm text-[#4B5563] mb-6">
                  &Ecirc;tes-vous s&ucirc;r de vouloir supprimer votre compte ? Cette action est irr&eacute;versible.
                </p>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setShowDeleteConfirm(false)}
                    data-testid="cancel-delete-button"
                  >
                    Annuler
                  </Button>
                  <Button
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white gap-2"
                    onClick={handleDeleteAccount}
                    disabled={deleting}
                    data-testid="confirm-delete-button"
                  >
                    {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    {deleting ? 'Suppression...' : 'Supprimer'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
